import React, { forwardRef, useCallback, useEffect, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faX } from '@fortawesome/free-solid-svg-icons';

type FlyOutMenuProps = {
    title: string;
    children: React.ReactNode;
    isOpen: boolean;
    onClose: () => void;
    width?: number;
    overflowY?: 'auto' | 'hidden' | 'visible' | 'scroll';
    ignoreClickRef?: React.RefObject<HTMLElement>;
};

const HEADER_HEIGHT = 60;

const FlyOutMenu = forwardRef<HTMLDivElement, FlyOutMenuProps>(
    ({ title, children, isOpen, onClose, width = 450, overflowY = 'hidden', ignoreClickRef }, ref) => {
        const [YOffset, setYOffset] = useState<number>(60);
        const internalRef = useRef<HTMLDivElement>(null);
        const flyOutRef = (ref as React.RefObject<HTMLDivElement>) || internalRef;

        const setScrollOffset = useCallback((): void => {
            const windowY = Math.floor(window.scrollY);
            if (windowY <= HEADER_HEIGHT) {
                setYOffset(HEADER_HEIGHT - windowY);
            }
        }, []);

        useEffect(() => {
            window.addEventListener('scroll', setScrollOffset);

            return (): void => {
                window?.removeEventListener('scroll', setScrollOffset);
            };
        }, [setScrollOffset]);

        useEffect(() => {
            if (!isOpen) return;

            const handleClickOutside = (event: MouseEvent): void => {
                if (flyOutRef.current && !flyOutRef.current.contains(event.target as HTMLElement)) {
                    if (ignoreClickRef?.current?.contains(event.target as HTMLElement)) {
                        return;
                    }
                    onClose();
                }
            };

            // Delay attaching listener to avoid the opening click from immediately closing
            const timeoutId = setTimeout(() => {
                document.addEventListener('mousedown', handleClickOutside);
            }, 0);

            return (): void => {
                clearTimeout(timeoutId);
                document.removeEventListener('mousedown', handleClickOutside);
            };
        }, [isOpen, onClose, flyOutRef, ignoreClickRef]);

        return (
            <div
                className="component-fly-out-menu"
                ref={flyOutRef}
                style={{
                    paddingTop: YOffset + 16,
                    width: `${width}px`,
                    right: isOpen ? 0 : `-${width + 3}px`,
                    overflowY,
                }}
            >
                <section className="fly-out-menu-header">
                    <h3
                        className="fly-out-menu-title"
                        title={title}
                    >
                        {title}
                    </h3>
                    <FontAwesomeIcon
                        icon={faX}
                        size="lg"
                        onClick={onClose}
                    />
                </section>
                <section className="fly-out-menu-body">{children}</section>
            </div>
        );
    },
);

FlyOutMenu.displayName = 'FlyOutMenu';

export default FlyOutMenu;
